import { V2VendorFeedSpecs } from 'new/components/flows/types/elt/CreateELTFlow/types';
import {
  V2_STRATEGIES,
  STRATEGY_DISPLAY_INFO,
  getDefaultStrategy,
  getAllStrategies,
} from '../constants/strategies';
import {
  ELT_CAPABILITIES,
  CapabilityChecker,
  CAPABILITY_DISPLAY_INFO,
  extractV2Capabilities,
  extractV2AdditionalParams,
} from '../constants/capabilities';
import { processPrimaryKeys } from './primaryKeyProcessor';
import { getDebugLogger } from 'new/hooks/useDebugLogger';

/**
 * V2 Template Processor
 *
 * This processor handles V2 ELT templates and creates the new unified structure
 * with strategy-based approach and capabilities system.
 *
 * Key Functions:
 * - Processes V2 capabilities config into unified capabilities structure
 * - Creates strategy-based ref_feed_options (vs V1's feed-based approach)
 * - Builds data_feed_options with rich feed information
 * - Extracts additional_params (non-capability parameters)
 * - Creates strategy mappings for UI consumption
 *
 * Input: vendor, availableFeeds, discoveryResults, processedEndpoints
 * Output: vendorFeedSpecs with unified capabilities + V2 structure
 */
// Debug logging is now controlled by the UI_ENABLE_DEBUGGER feature flag
const debugLogger = getDebugLogger();

// Local type definition for strategy mappings
interface StrategyMapping {
  [strategyName: string]: {
    //feed: string;
    ref_feeds: string[];
  };
}

/**
 * Get the parameter name for a capability from V2 capabilities config
 */
function getParamNameFromV2Capability(capabilityKey: string, capabilities: any): string | null {
  switch (capabilityKey) {
    case 'MULTI_SCHEMA':
      return capabilities?.multi_schema?.param_name || null;
    case 'OBJECT_DETECTION':
      return capabilities?.schema_discovery?.param_name || null;
    case 'FIELD_SELECTION':
      return capabilities?.field_selection?.field_list_selector_param || null;
    case 'LOOKBACK_TIME':
      return capabilities?.lookback_time?.param_name || null;
    default:
      return null;
  }
}

/**
 * Create a capability parameter based on the capability type
 */
function createCapabilityParameter(
  capabilityKey: string,
  paramName: string,
  displayInfo: any,
  capabilities: any,
): any {
  // Safety check for displayInfo
  if (!displayInfo || !displayInfo.name) {
    console.error(`Invalid displayInfo for capability ${capabilityKey}:`, displayInfo);
    return null;
  }

  const baseParam = {
    name: paramName,
    display_name: displayInfo.name,
    description: displayInfo.description || '',
    required: false,
    section: displayInfo.section || 'Configuration',
  };

  switch (capabilityKey) {
    case 'MULTI_SCHEMA':
      return {
        ...baseParam,
        type: 'select',
        options: [
          { label: 'Single Schema', value: 'true' },
          { label: 'Multi Schema', value: 'false' },
        ],
        default: 'true',
      };

    case 'OBJECT_DETECTION':
      return {
        ...baseParam,
        type: 'select',
        options: [
          { label: 'Enabled: Auto-Detect New Objects', value: 'true' },
          { label: 'Disabled: Only Ingest Selected Objects', value: 'false' },
        ],
        default: capabilities?.schema_discovery?.default || 'false',
      };

    case 'FIELD_SELECTION':
      return {
        ...baseParam,
        type: 'select',
        options: [
          { label: 'All Fields', value: 'all' },
          { label: 'Selected Fields', value: 'selected' },
        ],
      };

    case 'LOOKBACK_TIME':
      return {
        ...baseParam,
        type: 'string',
        default: capabilities?.lookback_time?.default || '{now-1}',
      };

    default:
      return null;
  }
}

export function processV2Template(
  vendor: any,
  availableFeeds: any[],
  discoveryResults: any,
  processedEndpoints: any, // The processed endpoints from FeedsList
  sourceConfigById: any, // The source config for feed_strategy_mapping
): { vendorFeedSpecs: V2VendorFeedSpecs; strategyMappings: StrategyMapping } {
  debugLogger.debugLog(2, 'Processing V2 template:', {
    vendor,
    availableFeeds,
    discoveryResults,
    processedEndpoints,
    sourceConfigById,
  });

  // Get the feeds endpoint from processed endpoints
  const feedsEndpoint = processedEndpoints.feeds?.[0];
  debugLogger.debugLog(2, 'vendor', vendor);
  debugLogger.debugLog(2, 'processedEndpoints', processedEndpoints);
  debugLogger.debugLog(2, 'feedsEndpoint', feedsEndpoint);
  debugLogger.debugLog(2, 'sourceConfigById', sourceConfigById);

  if (!feedsEndpoint) {
    console.warn('No feeds endpoint found for V2 template, falling back to V1 processing');
    return {
      vendorFeedSpecs: {
        ref_feed_options: {
          strategies: {},
          default_strategy: null,
          display: {},
          feed_names: {},
          ref_field_list_selector: '',
          ref_fields_names: '',
          default_ref_feed: null,
          default_include_feeds: [],
          supports_force_resync: {},
        },
        data_feed_options: {},
        strategyMappings: {},
        capabilities: {},
        additional_params: [],
      },
      strategyMappings: {},
    };
  }

  //first get all possible base_refs. Ideally they are catalogued in feedStrategyapping, but there might be a typo so we want to track them and add them to strategyMappings
  const allPossibleBaseRefs = Object.keys(feedsEndpoint?.source_template?.feeds) || [];

  const capabilities = feedsEndpoint.config?.capabilities || {};

  // Start iterating over keys of capabilities and map into ref_feed_options.
  // Map capabilities.feed_strategies to populate display, default_ref_feed, and strategies
  // Get the feed_strategy_mapping from resource_parameters
  const feedStrategyMappingParam = (feedsEndpoint?.resource_parameters || []).find(
    (param: any) => param.config?.param_type === 'feed_strategy_mapping',
  ) as any;

  debugLogger.debugLog(2, 'feed_strategy_mapping param found:', feedStrategyMappingParam);
  debugLogger.debugLog(2, 'resource_parameters:', feedsEndpoint?.resource_parameters);

  let strategyMappings: StrategyMapping = {};
  let mappedBaseRefs = new Set<string>();

  // Extract strategy mappings from the config object
  if (feedStrategyMappingParam?.config) {
    Object.values(V2_STRATEGIES).forEach((strategyName: string) => {
      const refFeeds = feedStrategyMappingParam.config[strategyName];
      if (refFeeds && Array.isArray(refFeeds)) {
        strategyMappings[strategyName] = { ref_feeds: refFeeds };
        // Add each ref_feed to the mapped set
        refFeeds.forEach((refFeed: string) => mappedBaseRefs.add(refFeed));
      }
    });
  }

  // Find base_refs in allPossibleBaseRefs that are not mapped
  const unmappedBaseRefs = allPossibleBaseRefs.filter((ref) => !mappedBaseRefs.has(ref));

  // Assign any unmapped base_refs to full_ingest_strategy
  if (unmappedBaseRefs.length > 0) {
    if (!strategyMappings[V2_STRATEGIES.FULL_INGEST]) {
      strategyMappings[V2_STRATEGIES.FULL_INGEST] = { ref_feeds: [...unmappedBaseRefs] };
    } else {
      // Add only the unmapped refs (avoid duplicates)
      const existingRefs = strategyMappings[V2_STRATEGIES.FULL_INGEST].ref_feeds;
      const newRefs = unmappedBaseRefs.filter((ref) => !existingRefs.includes(ref));
      strategyMappings[V2_STRATEGIES.FULL_INGEST].ref_feeds = [...existingRefs, ...newRefs];
    }
  }

  //defensive coding. Each key of feedsEndpoint.source_template.feeds is a base_ref and if it hasnt

  debugLogger.debugLog(2, 'feedStrategyMappingParam', feedStrategyMappingParam);
  debugLogger.debugLog(2, 'feedStrategyMapping', strategyMappings);

  // Extract all base_refs from the strategy mappings
  const allBaseRefs = Object.values(strategyMappings).flatMap((mapping) => mapping.ref_feeds);
  const uniqueBaseRefs = [...new Set(allBaseRefs)];

  debugLogger.debugLog(2, 'allBaseRefs', allBaseRefs);
  debugLogger.debugLog(2, 'uniqueBaseRefs', uniqueBaseRefs);

  // Get feed strategies from capabilities
  const feedStrategies = capabilities.feed_strategies || {};
  const supportedStrategies = feedStrategies.supported || [];
  const defaultStrategyFromCapabilities = feedStrategies.default || supportedStrategies[0];

  // Use centralized strategy constants, but filter to only supported ones
  const allStrategies = getAllStrategies();
  const supportedStrategiesList =
    supportedStrategies.length > 0
      ? supportedStrategies.filter((strategy) => allStrategies.includes(strategy as any))
      : allStrategies;

  debugLogger.debugLog(3, 'feedStrategies', feedStrategies);
  debugLogger.debugLog(3, 'supportedStrategies', supportedStrategies);
  debugLogger.debugLog(3, 'defaultStrategyFromCapabilities', defaultStrategyFromCapabilities);
  debugLogger.debugLog(3, 'supportedStrategiesList', supportedStrategiesList);

  // Build strategies object with only supported strategies
  const strategies = Object.fromEntries(
    supportedStrategiesList.map((strategy) => [
      strategy,
      {
        label: STRATEGY_DISPLAY_INFO[strategy as any]?.label || strategy,
        value: STRATEGY_DISPLAY_INFO[strategy as any]?.value || strategy,
      },
    ]),
  );

  // Get default strategy from capabilities or use centralized default
  const defaultStrategy =
    defaultStrategyFromCapabilities ||
    (supportedStrategiesList.length > 0 ? supportedStrategiesList[0] : getDefaultStrategy());

  // Build base_ref to strategy mapping for reverse lookup
  const baseRefToStrategy: { [baseRef: string]: string } = {};
  Object.entries(strategyMappings).forEach(([strategy, mapping]) => {
    mapping.ref_feeds.forEach((baseRef) => {
      baseRefToStrategy[baseRef] = strategy;
    });
  });

  // Build V1-compatible display and feed_names from base_refs (not strategies)
  const display: { [key: string]: { label: string; value: string; strategy: string } } = {};
  const feed_names: { [key: string]: { param: string } } = {};

  // Populate display and feed_names from base_refs
  // Handle feed_name_mapping as string or object with "default" key, fallback to 'feed_spec.object_name'
  let feedNameParam: string;
  if (typeof capabilities.feed_name_mapping === 'string') {
    feedNameParam = capabilities.feed_name_mapping;
  } else if (
    capabilities.feed_name_mapping &&
    typeof capabilities.feed_name_mapping === 'object' &&
    typeof capabilities.feed_name_mapping.default === 'string'
  ) {
    feedNameParam = capabilities.feed_name_mapping.default;
  } else {
    feedNameParam = 'feed_spec.object_name';
  }

  uniqueBaseRefs.forEach((baseRef) => {
    const strategy = baseRefToStrategy[baseRef];
    const strategyInfo = STRATEGY_DISPLAY_INFO[strategy as any];

    display[baseRef] = {
      label: strategyInfo?.label || baseRef,
      value: baseRef,
      strategy: strategy,
    };

    feed_names[baseRef] = { param: feedNameParam };
  });

  // do we support field name selection?
  let fieldSelectionEnabled = false,
    fieldListSelectorParam = '',
    fieldListParam = '';
  if ('field_selection' in capabilities && capabilities.field_selection?.enabled) {
    fieldSelectionEnabled = true;
    fieldListSelectorParam =
      capabilities.field_selection?.field_list_selector_param || 'feed_spec.nx_attr_force';
    fieldListParam =
      capabilities.field_selection?.field_list_param || 'feed_spec.nx_attr_force_attrs';
  }

  let objectDetectionModeSelectorParam, singleSchemaParam;
  if (
    'object_detection_mode_selector' in capabilities &&
    capabilities.object_detection_mode_selector?.enabled
  ) {
    objectDetectionModeSelectorParam =
      capabilities.object_detection_mode_selector?.param || 'feed_spec.object_detection_mode';
  }
  if ('single_schema' in capabilities && capabilities.single_schema?.enabled) {
    singleSchemaParam = capabilities.single_schema?.param || 'feed_spec.single_schema';
  }

  let supportsForceResync = {};
  //for each base ref, set force_resync support based on strategy type
  uniqueBaseRefs.forEach((baseRef) => {
    const strategy = baseRefToStrategy[baseRef];
    if (strategy === V2_STRATEGIES.AUTO_INCREMENT) {
      supportsForceResync[baseRef] = true;
    } else {
      // All other strategies (FULL_INGEST, etc.) do not support force_resync
      supportsForceResync[baseRef] = false;
    }
  });

  let defaultRefFeed =
    strategyMappings[defaultStrategy]?.ref_feeds?.[0] || uniqueBaseRefs[0] || null;

  // Build V1-compatible ref_feed_options using base_ref structure
  const refFeedOptions = {
    strategies,
    default_strategy: defaultStrategy,

    // V1-compatible properties computed from base_refs
    display,
    feed_names,
    ref_field_list_selector: fieldListSelectorParam,
    ref_fields_names: fieldListParam,
    default_ref_feed: defaultRefFeed,
    default_include_feeds: [],
    supports_force_resync: supportsForceResync,
    object_detection_mode_selector_param: objectDetectionModeSelectorParam,
    single_schema_param: singleSchemaParam,
    feed_name_param: feedNameParam,
  };

  // Process data feed options
  const dataFeedOptions: any = {};

  debugLogger.debugLog(3, '🔍 v2TemplateProcessor: Processing feeds:', {
    count: availableFeeds.length,
    feeds: 'feeds',
  });

  availableFeeds.forEach((feed: any) => {
    const feedKey = feed.feed_name || feed.name;

    // Process primary keys using JSONPath parsing for V2 templates
    // This handles the jsonpath<data-type> syntax (e.g., "$.primary_keys<array>")
    const processedPrimaryKeys = processPrimaryKeys(feed, feed.primary_keys);

    debugLogger.debugLog(
      3,
      `🔍 v2TemplateProcessor: Processing primary keys for feed ${feedKey}:`,
      {
        original: feed.primary_keys,
        processed: processedPrimaryKeys,
        feedData: feed,
      },
    );

    // Build V1-compatible primary key structure
    const primaryKeySelectors = {
      primary_key: processedPrimaryKeys[0] || '',
    };

    //pick the first ref_feed that is auto_increment_strategy, else full_ingest_strategy else incremental_v1_strategy, else first one
    // Optimize by finding once and reusing
    let defaultRef;
    if (Array.isArray(feed.allowed_reference_feeds) && feed.allowed_reference_feeds.length > 0) {
      const strategyPriority = [
        V2_STRATEGIES.AUTO_INCREMENT,
        V2_STRATEGIES.FULL_INGEST,
        V2_STRATEGIES.INCREMENTAL_V1,
      ];
      defaultRef = feed.allowed_reference_feeds.find((ref) =>
        strategyPriority.includes(ref.strategy),
      );
      if (!defaultRef) {
        defaultRef = feed.allowed_reference_feeds[0];
      }
    }

    // Get allowed strategies for this feed
    let feedParams = feed.feed_params || {};
    feedParams[refFeedOptions.feed_name_param] = feedKey;
    feedParams['ui.feed_display_name'] = feed.feed_display_name || feed.name || feedKey;

    dataFeedOptions[feedKey] = {
      ...feed,
      feed_name: feedKey,
      default_ref: defaultRef,
      primary_key_selectors: primaryKeySelectors,
      primary_keys: processedPrimaryKeys, // Store processed primary keys for V2 templates
      ref_feed_selectors: feed.allowed_reference_feeds, // Map allowed strategies to base_refs
      allowed_reference_feeds: feed.allowed_reference_feeds || [],
    };
  });

  // Extract unified capabilities structure from V2 config with embedded params
  // resourceParams should be feedsEndpoint?.resource_parameters plus start.cron and vendor_endpoint_id from sourceConfigById
  // sourceConfigById is an array; pluck params with name 'start.cron' or 'vendor_endpoint_id'
  const globalResourceParams = Array.isArray(sourceConfigById)
    ? sourceConfigById.filter(
        (param: any) => param?.name === 'start.cron' || param?.name === 'vendor_endpoint_id',
      )
    : [];
  const resourceParams = [...(feedsEndpoint?.resource_parameters || []), ...globalResourceParams];
  const unifiedCapabilities = extractV2Capabilities(
    feedsEndpoint?.config?.capabilities || {},
    resourceParams,
  );

  // Extract additional parameters (non-capability parameters like account_id, credentials)
  // For V2, we need to filter out capability-related parameters from resource_parameters
  const additionalParams = extractV2AdditionalParams(resourceParams, unifiedCapabilities);

  const vendorFeedSpecs: V2VendorFeedSpecs = {
    ref_feed_options: refFeedOptions,
    data_feed_options: dataFeedOptions,
    strategyMappings,
    capabilities: unifiedCapabilities,
    additional_params: additionalParams,
  };

  debugLogger.debugLog(2, '🔍 v2TemplateProcessor: V2 template processing result:', {
    vendorFeedSpecs,
    strategyMappings,
  });

  return { vendorFeedSpecs, strategyMappings };
}
